import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useSyncedPollingTick} from '../../state/polling/hooks';
import {type QueryConfig} from '../../state/runs/types';
import {useStatsigGateBucketedQuerySource} from '../../util/rampFeatureFlags/linePlotFeatureFlags';
import {BucketedQueryManager} from '../PanelRunsLinePlot/bucketedData/bucketedQueryManager';
import {type Range} from '../PanelRunsLinePlot/common';
import {createQueryStore} from '../PanelRunsLinePlot/QueryStore/createQueryStore';
import {type QueryStore} from '../PanelRunsLinePlot/QueryStore/queryStore';
import {type Zoom} from '../PanelRunsLinePlot/types';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {useParts} from './../../state/views/hooks';

export interface SharedPanelQueryState {
  query: QueryConfig | null;
  result: any;
}

interface SharedPanelStateContextType {
  bucketQueryManagerById: React.MutableRefObject<
    Record<string, BucketedQueryManager>
  >;
  queryStateById: Record<string, SharedPanelQueryState>;
  /**
   * Serves every runset's line-plot queries when the `query_tether_org` gate
   * or a URL override enabled the store as this provider mounted; null
   * otherwise, and the managers serve them.
   */
  queryStore: QueryStore | null;
  setQueryStateById: (runSetId: string, qs: SharedPanelQueryState) => void;
  setUserZoomState: React.Dispatch<React.SetStateAction<Zoom>>;
  setXDomainQuery: (xDomainQuery: Range) => void;
  userZoomState: Zoom;
  xDomainQuery: Range;
}

export const SharedPanelStateContext =
  createContext<SharedPanelStateContextType | null>(null);

const defaultQueryState = {
  query: null,
  result: null,
};

function useIds() {
  const {runSetRefs} = usePanelsSharedRefsContext();

  const runsets = useParts(runSetRefs);
  const runsetIds = useMemo(() => runsets.map(rs => rs.id), [runsets]);

  return runsetIds ?? [];
}

export const SharedPanelStateContextProvider = ({
  children,
  config,
  useRunsetIds = useIds,
  zoomState,
}: {
  children: React.ReactNode;
  config: {
    [key: string]: unknown;
    xAxisMin?: number | null;
    xAxisMax?: number | null;
  };
  useRunsetIds?: typeof useIds;
  /**
   * When we load a full screen panel we need to check and see if there are zoom parameters in the URL. If there are those are the default zoom values for the panel. Because we mount a SharedPanelStateContextProvider separately for different types of panels (fullscreen vs not-fullscreen) it's a reliable way to gate this behavior to only full screen panels.
   *
   * NOTE: This behavior exists because we need a way to syndicate zoom state when full screening a panel. A URL also allows a user to share that exact full screen panel zoom state to another user via the URL. However, there are some weird behaviors of this.
   * 1. A config zoomed panel (a panel with explict x/y ranges set in the config) will syndicate its values to the URL. And because the full screen panel shares DNA with the workspace panel if you edit the fullscreen panel you'll see the saved config values.
   * 2. a user zoomed panel (not a panel modified explicitly by x/y config zoom values) will syndicate its values to the URL but they will not be reflected in the edit panel modal when it's opened (it will snap back to config zoom values, if there are any). Changing those config values will change the zoom on the full screen panel, but it won't update the URL, nor will additional user zooming in a full screen panel. This breaks the connection between the URL being a sharable source of truth when in a full screen panel.
   *
   * TODO: SHould we fix this?
   * - should full screen panels syndicate params to the URL?
   * - should we think about getting rid of the URL?
   */
  zoomState?: Zoom;
}) => {
  const runsetIds = useRunsetIds();
  /**
   * Query states
   */
  const [queryStateById, setQueryState] = useState<
    Record<string, SharedPanelQueryState>
  >({});

  const setQueryStateById: SharedPanelStateContextType['setQueryStateById'] =
    useCallback(
      (runsetId, qs) => {
        setQueryState(prev => ({
          ...prev,
          [runsetId]: qs,
        }));
      },
      [setQueryState]
    );

  const bucketQueryManagerById = useRef<Record<string, BucketedQueryManager>>(
    {} as Record<string, BucketedQueryManager>
  );
  // Create managers during render. Child effects run before this provider's
  // effects, so a manager created in an effect would be missing when a panel
  // subscribes to a newly added runset.
  // oxlint-disable-next-line react/refs
  runsetIds.forEach(id => {
    if (!(id in bucketQueryManagerById.current)) {
      bucketQueryManagerById.current[id] = new BucketedQueryManager(id);
    }
  });

  // Chosen once per mount, so a mounted panel never switches between the
  // managers and the store. The store needs no cleanup: nothing outside
  // this provider holds it, and its work stops when consumers release their
  // subscriptions on unmount.
  const isQueryStoreGateOn = useStatsigGateBucketedQuerySource();
  const [queryStore] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const forceQueryStore =
      params.get('QUERY') === 'STORE' || params.get('QUERY_STORE') === 'true';
    return forceQueryStore || isQueryStoreGateOn ? createQueryStore() : null;
  });

  /**
   * Every time the runset IDs change we need to update the query states:
   * 1. Remove any query states that aren't active
   * 2. Add any new query states that are active
   * 3. Remove any bucket managers that aren't active
   */
  useEffect(() => {
    const existingBucketManagers = Object.keys(bucketQueryManagerById.current);

    // Remove any bucket managers that aren't referenced
    existingBucketManagers.forEach(id => {
      if (!runsetIds.includes(id)) {
        delete bucketQueryManagerById.current[id];
      }
    });

    setQueryState(prevQs => {
      const newState = {...prevQs};

      // remove any query states that aren't active
      Object.keys(newState).forEach(id => {
        if (!runsetIds.includes(id)) {
          delete newState[id];
        }
      });

      // Either preserve the state or add a new one for the active runsets
      runsetIds.forEach(id => {
        newState[id] = prevQs[id] ? prevQs[id] : {...defaultQueryState};
      });

      return newState;
    });
  }, [runsetIds]);

  const [userZoomState, setUserZoomState] = useState<Zoom>(zoomState ?? {});
  const [xDomainQuery, setXDomainQuery] = useState<Range>({
    min: config.xAxisMin ?? null,
    max: config.xAxisMax ?? null,
  });
  const setXDomainQueryChecked = useCallback(
    (xDomainQuery: Range) => {
      setXDomainQuery(curVal =>
        curVal.min === xDomainQuery.min && curVal.max === xDomainQuery.max
          ? curVal
          : xDomainQuery
      );
    },
    [setXDomainQuery]
  );

  useSyncedPollingTick(
    'BucketedQueryManager',
    function pollBucketedQueryManagers() {
      Object.values(bucketQueryManagerById.current).forEach(bqm => {
        bqm.poll();
      });
      queryStore?.poll();
    }
  );

  const value = useMemo(
    () => ({
      bucketQueryManagerById,
      queryStateById,
      queryStore,
      setQueryStateById,
      setUserZoomState,
      setXDomainQuery: setXDomainQueryChecked,
      userZoomState,
      xDomainQuery,
    }),
    [
      bucketQueryManagerById,
      queryStateById,
      queryStore,
      setQueryStateById,
      setXDomainQueryChecked,
      userZoomState,
      xDomainQuery,
    ]
  );

  return (
    <SharedPanelStateContext.Provider value={value}>
      {children}
    </SharedPanelStateContext.Provider>
  );
};

export const useSharedPanelState = (): SharedPanelStateContextType => {
  const ctx = useContext(SharedPanelStateContext);
  if (ctx == null) {
    throw new Error(
      'useSharedPanelState must be used within a SharedPanelStateContextProvider'
    );
  }
  return ctx;
};

export const useSharedPanelStateMaybe =
  (): SharedPanelStateContextType | null => {
    const ctx = useContext(SharedPanelStateContext);
    return ctx;
  };
